<template>
  <div class="shakespeare-view">
    <!-- 返回按钮 -->
    <div class="back-button-container">
      <el-button @click="goBack" class="back-button">
        <el-icon><ArrowLeft /></el-icon>
        返回
      </el-button>
    </div>
    
    <!-- 主要内容区域：左右分栏 -->
    <div class="main-layout">
      <!-- 左侧对话面板 -->
      <div class="chat-panel">
        <!-- 紫色标题栏 -->
        <div class="chat-header">
          <h3>莎士比耶</h3>
        </div>
        <div class="chat-container">
          <div class="chat-messages" ref="chatMessagesContainer">
            <div v-for="(message, index) in chatMessages" :key="index" class="message-row" :class="message.type === 'user' ? 'message-right' : 'message-left'">
              <div v-if="message.type === 'assistant'" class="message-avatar">
                <div class="avatar-shakespeare">
                  <img src="/img/logo.43a75e94.jpg" alt="Shakespeare" />
                </div>
              </div>
              <div class="message-content-wrapper">
                <div class="message-bubble" :class="message.type === 'user' ? 'bubble-user' : 'bubble-shakespeare'">
                  <div class="message-content">{{ message.content }}</div>
                </div>
              </div>
              <div v-if="message.type === 'user'" class="message-avatar">
                <div class="avatar-user">我</div>
              </div>
            </div>
            <div v-if="chatMessages.length === 0" class="no-messages">
              <div class="welcome-content">
                <p>开始与大纲助手对话吧！</p>
              </div>
            </div>
          </div>
          
          <div class="chat-input-area">
            <div class="input-container">
              <el-input
                v-model="inputMessage"
                type="textarea"
                :rows="3"
                placeholder="输入您的问题或寻求建议..."
                resize="none"
                @keydown.enter.prevent="handleSendMessage"
              />
            </div>
            <div class="button-container">
              <el-button 
                type="primary" 
                @click="handleSendMessage"
                :disabled="!inputMessage.trim() || isGenerating"
                :loading="isGenerating"
                class="send-button"
              >
                {{ isGenerating ? '思考中...' : '发送' }}
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧布局区域 -->
      <div class="layout-container">
        <div class="layout-panel">
          <!-- 左侧大div (1div) -->
          <div class="layout-column-1">
            <div class="layout-item item-1">段落</div>
          </div>
          
          <!-- 中间大div (2div) -->
          <div class="layout-column-2">
            <div class="layout-item item-2">分集大纲</div>
            <div class="layout-item item-2">分集大纲</div>
          </div>
          
          <!-- 右侧大div (3div) -->
          <div class="layout-column-3">
            <div class="layout-item item-3">详细剧本</div>
            <div class="layout-item item-3">详细剧本</div>
          </div>
        </div>
        
        <!-- 可以添加更多的layout-panel -->
        <div class="layout-panel">
          <!-- 左侧大div (1div) -->
          <div class="layout-column-1">
            <div class="layout-item item-1">段落</div>
          </div>
          
          <!-- 中间大div (2div) -->
          <div class="layout-column-2">
            <div class="layout-item item-2">分集大纲</div>
            <div class="layout-item item-2">分集大纲</div>
          </div>
          
          <!-- 右侧大div (3div) -->
          <div class="layout-column-3">
            <div class="layout-item item-3">详细剧本</div>
            <div class="layout-item item-3">详细剧本</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowLeft } from '@element-plus/icons-vue'

const router = useRouter()

// 响应式数据
const inputMessage = ref('')
const chatMessages = ref([
  {
    type: 'assistant',
    content: '你好！我是莎士比耶，很高兴与你相遇。无论你是想探讨文学创作的奥秘，还是需要写作技巧的指导，我都愿意与你分享我的见解。请告诉我，你希望我如何帮助你？'
  }
])
const isGenerating = ref(false)
const chatMessagesContainer = ref(null)

// 发送消息
const handleSendMessage = async () => {
  if (!inputMessage.value.trim() || isGenerating.value) return
  
  const userMessage = inputMessage.value.trim()
  
  // 添加用户消息
  chatMessages.value.push({
    type: 'user',
    content: userMessage
  })
  
  inputMessage.value = ''
  isGenerating.value = true
  
  // 滚动到底部
  await nextTick()
  scrollToBottom()
  
  try {
    // 模拟AI回复（这里可以接入真实的AI API）
    setTimeout(() => {
      const responses = [
        '正如我在《哈姆雷特》中所写："生存还是毁灭，这是一个问题。" 在写作中，我们也面临着选择——是追求华丽的辞藻，还是朴实的真情？我建议你...',
        '亲爱的朋友，写作如同舞台上的表演，需要真情实感。正如我常说："整个世界是一个舞台，所有的男男女女不过是一些演员。" 让你的文字也成为舞台上的精彩演出吧！',
        '在我的创作生涯中，我发现最动人的故事往往来自于人性的复杂与矛盾。就像《罗密欧与朱丽叶》中的爱恨交织，真正的文学作品应该触及人心最深处...',
        '我的朋友，语言是思想的外衣。正如我在十四行诗中所追求的，每一个词汇都应该精确而有力。让我们一起探讨如何让你的文字更加生动有力...'
      ]
      
      const randomResponse = responses[Math.floor(Math.random() * responses.length)]
      
      chatMessages.value.push({
        type: 'assistant',
        content: randomResponse
      })
      
      isGenerating.value = false
      nextTick(() => scrollToBottom())
    }, 1500)
  } catch (error) {
    console.error('发送消息失败:', error)
    isGenerating.value = false
  }
}

// 滚动到底部
const scrollToBottom = () => {
  if (chatMessagesContainer.value) {
    chatMessagesContainer.value.scrollTop = chatMessagesContainer.value.scrollHeight
  }
}

// 返回上一页
const goBack = () => {
  router.go(-1)
}
</script>

<style scoped>
/* 返回按钮容器 */
.back-button-container {
  padding-bottom: 8px;
}

.back-button {
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.back-button:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #475569;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06);
}

.back-button:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* 主容器 */
.shakespeare-view {
  padding: 0 24px 24px 24px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}



/* 主要布局：左右分栏 */
.main-layout {
  display: flex;
  gap: 24px;
  height: calc(100vh - 120px);
  overflow: hidden;
}

/* 左侧对话面板 */
.chat-panel {
  width: 25%;
  min-width: 320px;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 16px;
  border: 2px solid #e0e0e0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* 紫色标题栏 */
.chat-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 16px 20px;
  text-align: center;
}

.chat-header h3 {
  margin: 0;
  color: white;
  font-size: 18px;
  font-weight: 500;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  flex: 1;
  overflow: hidden;
}

.chat-messages {
  height: 80%;
  overflow-y: auto;
  padding: 20px;
  background: #f5f5f5;
  flex-shrink: 1;
}

.message-row {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
  gap: 12px;
}

.message-left {
  justify-content: flex-start;
}

.message-right {
  justify-content: flex-end;
}

.message-avatar {
  flex-shrink: 0;
}

.avatar-shakespeare {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
  overflow: hidden;
}

.avatar-shakespeare img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.avatar-user {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: white;
}

.message-content-wrapper {
  max-width: 70%;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 12px;
  word-wrap: break-word;
  line-height: 1.5;
}

.bubble-shakespeare {
  background: white;
  border: 1px solid #e0e0e0;
  color: #333;
}

.bubble-user {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: #999;
}

.welcome-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.welcome-content p {
  font-size: 16px;
  color: #888;
  margin: 0;
  text-align: center;
}

.chat-input-area {
  height: 20%;
  min-height: 120px;
  padding: 10px 10px 5px 10px;
  background: white;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 9px;
  flex-shrink: 0;
  overflow: visible;
}

.input-container {
  width: 100%;
}

.input-container :deep(.el-textarea__inner) {
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}
module-list
.input-container .el-textarea {
  border: none;
  box-shadow: none;
}

.input-container .el-textarea__inner {
  border: none;
  box-shadow: none;
  background: transparent;
  resize: none;
  padding: 8px 0;
  font-size: 14px;
  line-height: 1.4;
}

.input-container .el-textarea__inner:focus {
  border: none;
  box-shadow: none;
  outline: none;
}

.button-container {
  display: flex;
  justify-content: flex-end;
}

.send-button {
  height: 36px;
  padding: 0 20px;
  border-radius: 18px;
}

/* 右侧布局容器 */
.layout-container {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* 滚动条样式 */
.layout-container::-webkit-scrollbar {
  width: 8px;
}

.layout-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.layout-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.layout-container::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* 右侧布局面板 */
.layout-panel {
  background: white;
  border-radius: 16px;
  display: flex;
  padding: 0px 0px 20px 0px;
  gap: 20px;
  align-items: flex-start;
  min-height: fit-content;
  height: auto;
  flex-shrink: 0;
}

/* 三列布局样式 */
.layout-column-1 {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.layout-column-2 {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.layout-column-3 {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 布局项目样式 */
.layout-item {
  border: 2px solid #ff4444;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  color: #ff4444;
  background: white;
}

/* 1div样式 - 与最上面的2div对齐 */
.item-1 {
  width: 520px;
  height: 310px;
}

/* 2div样式 */
.item-2 {
  width: 742px;
  height: 403px;
}

/* 3div样式 - 与2div对齐 */
.item-3 {
  width: 627px;
  height: 403px;
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .main-layout {
    flex-direction: column;
    height: auto;
  }
  
  .chat-panel {
    min-width: auto;
    height: 500px;
  }
  
  .scroll-panel {
    width: 100%;
    height: 300px;
  }
  
  .scroll-content {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
  }
}

@media (max-width: 768px) {
  .shakespeare-view {
    padding: 16px;
  }
  
  .main-layout {
    gap: 16px;
  }
  
  .chat-panel {
    height: 400px;
  }
  
  .scroll-panel {
    height: 250px;
  }
  
  .message-content-wrapper {
    max-width: 85%;
  }
  
  .scroll-item {
    width: 160px;
  }
  
  .tool-card {
    height: 120px;
    padding: 16px;
  }
  
  .tool-icon {
    font-size: 24px;
  }
  
  .tool-card h4 {
    font-size: 14px;
  }
  
  .tool-card p {
    font-size: 11px;
  }
}
</style>